import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, EmptyState, Skeleton } from "@akuna/ui";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useCatalogGroups, useCatalogItems, useCommissionModels } from "@/hooks/useCommissions";
import { useItems } from "@/hooks/useItems";
import { attachSalePrices } from "@/lib/commissions/tree";
import type { CommissionModel } from "@/lib/commissions/types";
import { TeamAccessDenied } from "@/pages/team/TeamAccessDenied";
import { AssignModelDialog } from "@/components/commissions/AssignModelDialog";
import { CatalogSectionNav } from "./CatalogSectionNav";
import { CommissionModelEditor } from "./CommissionModelEditor";
import { CommissionModelList } from "./CommissionModelList";
import { NewModelDialog } from "./NewModelDialog";

export default function CatalogCommissionsPage() {
  const { t } = useTranslation("commissions");
  const { hasPermission: canView, isLoading } = useHasPermissionSync("commissions", "view");
  const { hasPermission: canCreate } = useHasPermissionSync("commissions", "create");
  const { hasPermission: canEdit } = useHasPermissionSync("commissions", "edit");
  const { data: models = [], isLoading: loadingModels } = useCommissionModels(canView);
  const { data: groups = [] } = useCatalogGroups(canView);
  const { data: catalogItems = [] } = useCatalogItems(canView);
  const { data: itemsWithPrices } = useItems({ isActive: true }, canView);
  const items = useMemo(() => {
    const catalogRows = Array.isArray(itemsWithPrices) ? itemsWithPrices : itemsWithPrices?.data ?? [];
    return attachSalePrices(catalogItems, catalogRows);
  }, [catalogItems, itemsWithPrices]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modelDialog, setModelDialog] = useState<{ open: boolean; model: CommissionModel | null }>({ open: false, model: null });
  const [assignOpen, setAssignOpen] = useState(false);

  if (isLoading) return null;
  if (!canView) return <TeamAccessDenied />;

  const selected = models.find((m) => m.id === selectedId) ?? models.find((m) => m.is_active) ?? models[0] ?? null;
  const openNew = () => setModelDialog({ open: true, model: null });

  return (
    <div className="space-y-5">
      <div className="hidden md:block">
        <h1 className="text-2xl font-bold text-foreground">{t("catalog.title")}</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("catalog.intro")}</p>
      </div>
      <CatalogSectionNav active="commissions" />

      {loadingModels ? (
        <Skeleton label={t("catalog.models.loading")} className="h-96 w-full" />
      ) : !selected ? (
        <div className="rounded-card border border-border bg-card">
          <EmptyState
            title={t("catalog.models.empty")}
            action={canCreate ? <Button onClick={openNew}>{t("catalog.models.new")}</Button> : undefined}
          />
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-5">
          <CommissionModelList
            models={models}
            selectedId={selected.id}
            onSelect={setSelectedId}
            onCreate={canCreate ? openNew : undefined}
          />
          <CommissionModelEditor
            key={selected.id}
            model={selected}
            groups={groups}
            items={items}
            canEdit={canEdit}
            onEdit={() => setModelDialog({ open: true, model: selected })}
            onAssign={() => setAssignOpen(true)}
          />
        </div>
      )}

      <NewModelDialog
        open={modelDialog.open}
        model={modelDialog.model}
        onOpenChange={(open) => setModelDialog((d) => ({ ...d, open }))}
        onSaved={(id) => setSelectedId(id)}
      />
      <AssignModelDialog model={selected} open={assignOpen} onOpenChange={setAssignOpen} />
    </div>
  );
}
